// src/components/charts/DurationHistogram.jsx
//
// Histogram of run duration = completed_at - created_at, per row.
// Picks units (s vs min) based on the median: if median < 120s → seconds; else minutes.
// Rows missing either timestamp are dropped + counted in console.warn once.
//
import React from 'react';
import { CHART_SERIES, COLORS, FONT_SANS } from '../../constants/theme.js';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';

/**
 * Compute duration in seconds for each row that has both timestamps.
 * Returns array of seconds (sorted ascending — caller bins it).
 * Exported for unit testing.
 */
export function durationSeconds(runs) {
  if (!Array.isArray(runs)) return [];
  const out = [];
  for (const r of runs) {
    if (!r?.created_at || !r?.completed_at) continue;
    const start = new Date(r.created_at).getTime();
    const end   = new Date(r.completed_at).getTime();
    if (Number.isNaN(start) || Number.isNaN(end) || end <= start) continue;
    out.push((end - start) / 1000);
  }
  return out.sort((a, b) => a - b);
}

function median(sortedAsc) {
  const n = sortedAsc.length;
  if (n === 0) return 0;
  const mid = Math.floor(n / 2);
  return n % 2 === 0 ? (sortedAsc[mid - 1] + sortedAsc[mid]) / 2 : sortedAsc[mid];
}

/**
 * Bin a sorted-asc array of seconds into 8 quantile-ish buckets.
 * Picks unit (s vs min) based on median.
 * Exported for unit testing.
 */
export function bucketDurations(sortedSeconds) {
  if (sortedSeconds.length === 0) return { bins: [], unit: 's' };
  const med = median(sortedSeconds);
  const useMinutes = med >= 120;
  const max = sortedSeconds[sortedSeconds.length - 1];
  const divisor = useMinutes ? 60 : 1;
  const maxUnit = max / divisor;
  const binCount = 8;
  const step = Math.max(1, Math.ceil(maxUnit / binCount));
  const bins = Array.from({ length: binCount }, (_, i) => ({
    bucket: `${i * step}-${(i + 1) * step}`,
    count: 0,
  }));
  for (const s of sortedSeconds) {
    const v = s / divisor;
    const idx = Math.min(binCount - 1, Math.floor(v / step));
    bins[idx].count += 1;
  }
  return { bins, unit: useMinutes ? 'min' : 's' };
}

export default function DurationHistogram({ runs }) {
  const secs = durationSeconds(runs);
  const { bins, unit } = bucketDurations(secs);
  if (bins.length === 0 || bins.every(b => b.count === 0)) return null;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={bins} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
        <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: COLORS.textSecondary }} label={{ value: unit, position: 'insideBottomRight', offset: -2, fontSize: 11, fill: COLORS.textSecondary }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: COLORS.textSecondary }} />
        <Tooltip contentStyle={{ fontSize: 12, fontFamily: FONT_SANS }} />
        <Bar dataKey="count" fill={CHART_SERIES[0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
